import React, { useState, useEffect } from 'react'
import { Edit2, Save, X, Plus, Trash2, RefreshCw } from 'lucide-react'
import { projectsAPI } from '../services/api'
import './EditableProjectTable.css'

interface Project {
  id?: number
  client_name: string
  project_common_name?: string
  agreement_status?: string
  start_date?: string
  partner_agreements?: string
  project_status?: string
  timeline_to_resume?: string
  nature_of_work?: string
  relationship_manager?: string
  account_manager?: string
  billed?: string
  monthly_retainer?: number
  months_of_revenue?: number
  expected_fiat_revenue?: number
  token_potential?: number
  billing_health?: number
  tge_date?: string
  priority?: number
  auto?: boolean
  notes?: string
  core_hours?: string
  engagement_style?: string
  legal_notes?: string
  created_at?: string
  updated_at?: string
}

const EditableProjectTable: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [lastSync, setLastSync] = useState<Date | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)

  // Load projects from API
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setIsSyncing(true)
        setLoading(true)
        const response = await projectsAPI.getAll()
        if (response.success) {
          setProjects(response.data || [])
          setLastSync(new Date())
        } else {
          setError('Failed to load projects')
        }
      } catch (err) {
        setError('Error loading projects')
        console.error('Error loading projects:', err)
      } finally {
        setLoading(false)
        setIsSyncing(false)
      }
    }

    // Initial load
    loadProjects()

    // Set up real-time sync - refresh data every 30 seconds
    const syncInterval = setInterval(loadProjects, 30000)

    // Cleanup interval on unmount
    return () => clearInterval(syncInterval)
  }, [])

  const startEditing = (project: Project) => {
    setEditingId(project.id || null)
    setEditingProject({ ...project })
  }

  const cancelEditing = () => {
    // If we were editing a new project, remove it from the list
    if (editingId === 'new') {
      setProjects(projects.filter(p => p.id !== undefined))
    }
    setEditingId(null)
    setEditingProject(null)
  }

  const saveProject = async () => {
    if (!editingProject) return

    try {
      if (editingProject.id) {
        // Update existing project
        const response = await projectsAPI.update(editingProject.id.toString(), editingProject)
        if (response.success) {
          setProjects(projects.map(p => p.id === editingProject.id ? response.data : p))
        }
      } else {
        // Create new project
        const response = await projectsAPI.create(editingProject)
        if (response.success) {
          setProjects([...projects, response.data])
        }
      }
      setEditingId(null)
      setEditingProject(null)
    } catch (err) {
      console.error('Error saving project:', err)
      setError('Failed to save project')
    }
  }

  const deleteProject = async (id: number) => {
    if (confirm('Are you sure you want to delete this project?')) {
      try {
        await projectsAPI.delete(id.toString())
        setProjects(projects.filter(p => p.id !== id))
      } catch (err) {
        console.error('Error deleting project:', err)
        setError('Failed to delete project')
      }
    }
  }

  const addNewProject = () => {
    const newProject: Project = {
      client_name: '',
      project_common_name: '',
      agreement_status: 'Pre-agreement',
      start_date: '',
      partner_agreements: '',
      project_status: 'Pending',
      timeline_to_resume: '',
      nature_of_work: 'Token Design',
      relationship_manager: '',
      account_manager: '',
      billed: 'Monthly',
      monthly_retainer: 0,
      months_of_revenue: 0,
      expected_fiat_revenue: 0,
      token_potential: 0,
      billing_health: 1,
      tge_date: '',
      priority: 1,
      auto: false,
      notes: '',
      core_hours: 'US',
      engagement_style: '',
      legal_notes: '',
    }
    const updatedProjects = [...projects, newProject]
    setProjects(updatedProjects)
    setEditingId('new') // Use 'new' as special identifier for new projects
    setEditingProject({ ...newProject })
    
    // Scroll to the new row after a brief delay
    setTimeout(() => {
      const newRow = document.querySelector('.editing-row')
      if (newRow) {
        newRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }

  const updateEditingField = (field: keyof Project, value: any) => {
    if (!editingProject) return
    setEditingProject({ ...editingProject, [field]: value })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800'
      case 'Paused': return 'bg-yellow-100 text-yellow-800'
      case 'Pending': return 'bg-blue-100 text-blue-800'
      case 'Completed': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getAgreementStatusColor = (status: string) => {
    switch (status) {
      case 'Agreement Completed': return 'bg-green-100 text-green-800'
      case 'Started // Pre-agreement': return 'bg-yellow-100 text-yellow-800'
      case 'Pre-agreement': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getWorkTypeColor = (type: string) => {
    switch (type) {
      case 'Token Design': return 'bg-purple-100 text-purple-800'
      case 'Multi-discipline': return 'bg-indigo-100 text-indigo-800'
      case 'Market Making/ CEX': return 'bg-pink-100 text-pink-800'
      case 'Fundraising': return 'bg-red-100 text-red-800'
      case 'Treasury Management': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Dropdown options for various fields
  const agreementStatusOptions = [
    'Agreement Completed',
    'Started // Pre-agreement', 
    'Pre-agreement'
  ]

  const projectStatusOptions = [
    'Active',
    'Paused',
    'Pending',
    'Completed'
  ]

  const natureOfWorkOptions = [
    'Token Design',
    'Multi-discipline',
    'Market Making/ CEX',
    'Fundraising',
    'Treasury Management'
  ]

  const billedOptions = [
    'Monthly',
    'Once Off'
  ]

  const coreHoursOptions = [
    'US',
    'GLB (Late PST)',
    'HK',
    'UK',
    'EU/GLB'
  ]

  const engagementStyleOptions = [
    'Ideation',
    'Baking Product',
    'Many Cooks',
    'Flakey',
    'Discussion Driven',
    'Bespoke'
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading projects...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Project Management</h2>
            <p className="text-gray-600">Fully editable project tracking system</p>
          </div>
          
          <div className="flex space-x-3">
            <button 
              onClick={addNewProject} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus size={16} />
              <span>Add New Project</span>
            </button>
            <button 
              onClick={() => {
                const loadProjects = async () => {
                  try {
                    setIsSyncing(true)
                    setLoading(true)
                    const response = await projectsAPI.getAll()
                    if (response.success) {
                      setProjects(response.data || [])
                      setLastSync(new Date())
                    } else {
                      setError('Failed to load projects')
                    }
                  } catch (err) {
                    setError('Error loading projects')
                    console.error('Error loading projects:', err)
                  } finally {
                    setLoading(false)
                    setIsSyncing(false)
                  }
                }
                loadProjects()
              }} 
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        
        {/* Sync Status Indicator */}
        <div className="text-sm text-gray-500">
          {isSyncing && <span className="flex items-center space-x-2">🔄 Syncing with database...</span>}
          {lastSync && <span>Last sync: {lastSync.toLocaleTimeString()}</span>}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agreement Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nature of Work</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Retainer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((project, index) => {
                const isNewProject = editingId === 'new' && index === projects.length - 1
                const isEditing = editingId === project.id || isNewProject
                
                return (
                  <tr key={project.id || `new-${index}`} className={isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    {/* Client Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingProject?.client_name || ''}
                          onChange={(e) => updateEditingField('client_name', e.target.value)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="text-sm font-medium text-gray-900">{project.client_name}</div>
                      )}
                    </td>

                    {/* Project Common Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingProject?.project_common_name || ''}
                          onChange={(e) => updateEditingField('project_common_name', e.target.value)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">{project.project_common_name}</div>
                      )}
                    </td>

                    {/* Agreement Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <select
                          value={editingProject?.agreement_status || ''}
                          onChange={(e) => updateEditingField('agreement_status', e.target.value)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {agreementStatusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAgreementStatusColor(project.agreement_status || '')}`}>
                          {project.agreement_status}
                        </span>
                      )}
                    </td>

                    {/* Start Date */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingProject?.start_date || ''}
                          onChange={(e) => updateEditingField('start_date', e.target.value)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">{project.start_date}</div>
                      )}
                    </td>

                    {/* Project Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <select
                          value={editingProject?.project_status || ''}
                          onChange={(e) => updateEditingField('project_status', e.target.value)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {projectStatusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.project_status || '')}`}>
                          {project.project_status}
                        </span>
                      )}
                    </td>

                    {/* Nature of Work */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <select
                          value={editingProject?.nature_of_work || ''}
                          onChange={(e) => updateEditingField('nature_of_work', e.target.value)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {natureOfWorkOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getWorkTypeColor(project.nature_of_work || '')}`}>
                          {project.nature_of_work}
                        </span>
                      )}
                    </td>

                    {/* Monthly Retainer */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editingProject?.monthly_retainer || ''}
                          onChange={(e) => updateEditingField('monthly_retainer', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">${project.monthly_retainer?.toLocaleString()}</div>
                      )}
                    </td>

                    {/* Expected Fiat Revenue */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editingProject?.expected_fiat_revenue || ''}
                          onChange={(e) => updateEditingField('expected_fiat_revenue', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">${project.expected_fiat_revenue?.toLocaleString()}</div>
                      )}
                    </td>

                    {/* Priority */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editingProject?.priority || ''}
                          onChange={(e) => updateEditingField('priority', parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">{project.priority}</div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {isEditing ? (
                        <div className="flex space-x-2">
                          <button 
                            onClick={saveProject} 
                            className="text-green-600 hover:text-green-900"
                          >
                            <Save size={16} />
                          </button>
                          <button 
                            onClick={cancelEditing} 
                            className="text-red-600 hover:text-red-900"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => startEditing(project)} 
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit2 size={16} />
                          </button>
                          {project.id && (
                            <button 
                              onClick={() => project.id && deleteProject(project.id)} 
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default EditableProjectTable 